/**
 * @file Google Tag Manager higher order component.
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { connect } from 'react-redux';
import { matchPath, withRouter } from 'react-router-dom';
import TagManager from 'react-gtm-module';

import { MeQuery } from '../../graphql/queries/account';
import getConfig from '../../util/config';
import { getDefaultProRoleFromAccount } from '../../util/account';
import ACCOUNT_ROLES from '../../constants/account-roles';

const propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

const IGNORED_PATHS = [
  '/cards/print/:accountCardId/:side',
  '/marketing-materials/print/:accountMarketingMaterialId',
];

const withGoogleTagManager = (WrappedComponent, options = {}) => {
  // GTM setup & initialization
  const config = getConfig();
  const { tagManagerId } = config?.google ?? {};
  const { baseUrl } = config?.app ?? {};

  if (!tagManagerId) {
    return WrappedComponent;
  }

  // HOC tracking page views and users
  const GoogleTagManagerHOC = props => {
    const { location, user } = props;
    const userId = user?.id ?? null;

    const isIgnoredPath = !!IGNORED_PATHS.find(path =>
      matchPath(location.pathname, { path, exact: true }),
    );

    // Initialize GTM on first render.
    useEffect(() => {
      if (isIgnoredPath) {
        return;
      }

      TagManager.initialize({
        gtmId: tagManagerId,
        ...options,
      });
    }, []);

    // Track a page view every time the pathname changes.
    useEffect(() => {
      if (isIgnoredPath) {
        return;
      }

      const newPageData = {
        url: `${baseUrl}${location.pathname}${location.search}`,
        title: window.document.title,
        userId: userId || '',
      };

      TagManager.dataLayer({
        dataLayer: {
          event: 'newPage',
          newPageData,
        },
      });

      if (userId) {
        // Add support for HotJar Identify
        // https://help.hotjar.com/hc/en-us/articles/360038394053
        window.hj?.('identify', userId, {
          'Account created': user?.created_at || null,
        });

        // Add support for Stonly Identify
        // https://stonly.com/kb/guide/en/install-the-tracking-script-for-stonly-targeting-F0GPxJNVDm/Steps/83232
        window?.stonlyTrack?.('identify', userId, {
          createdat: user.created_at,
          usertype:
            getDefaultProRoleFromAccount(user) || ACCOUNT_ROLES.SHUTTERBUG,
        });
      }
    }, [location.pathname]);

    return <WrappedComponent {...props} />;
  };

  GoogleTagManagerHOC.propTypes = propTypes;

  const mapStateToProps = ({ auth }) => ({
    authenticated: auth.authenticated,
  });

  const withUserData = graphql(MeQuery, {
    props: ({ data }) => ({
      user: data.me,
    }),
    skip: ({ authenticated }) => !authenticated,
  });

  return compose(
    withRouter,
    connect(mapStateToProps),
    withUserData,
  )(GoogleTagManagerHOC);
};

export default withGoogleTagManager;
